import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { Link as RouterLink } from "react-router-dom";
import * as Yup from "yup";
import { Formik } from "formik";

import {
  Alert as MuiAlert,
  Button as MuiButton,
  TextField as MuiTextField,
  Typography,
  Link,
} from "@mui/material";
import { spacing } from "@mui/system";

import useAuth from "@/hooks/useAuth";
import { verifyInvitationRequest } from "apiRequests/users";
import { useQueryParams } from "@/hooks/useQuery";


const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

const Button = styled(MuiButton)(spacing);

const Centered = styled(Typography)`
  text-align: center;
`;

function SignUp() {
  const params = useQueryParams();
  const navigate = useNavigate();
  const { signUp } = useAuth();

  // Older invitation emails linked to /auth/sign-up?token=CODE. If a student
  // still arrives via such a link, pre-fill the code field for convenience —
  // but the code can also simply be typed in.
  const prefilledCode = (params.get("token") ?? "").trim().toUpperCase();

  return (
    <Formik
      initialValues={{
        email: "",
        invitationCode: prefilledCode,
        password: "",
        confirmPassword: "",
        submit: false,
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .email("Must be a valid email")
          .max(255)
          .required("Email is required"),
        invitationCode: Yup.string()
          .trim()
          .required("Invitation code is required"),
        password: Yup.string()
          .min(12, "Must be at least 12 characters")
          .max(255)
          .matches(/[a-z]/, "Must contain a lowercase letter")
          .matches(/[A-Z]/, "Must contain an uppercase letter")
          .matches(/\d/, "Must contain a number")
          .matches(/[^a-zA-Z0-9]/, "Must contain a special character")
          .required("Required"),
        confirmPassword: Yup.string().oneOf(
          // @ts-expect-error null not assignable
          [Yup.ref("password"), null],
          "Passwords must match"
        ),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          const email = values.email.trim().toLowerCase();
          const invitationCode = values.invitationCode.trim().toUpperCase();

          // Gate on the invitation code before creating an account.
          const { data } = await verifyInvitationRequest(email, invitationCode);
          if (!data?.valid) {
            throw new Error(
              "That invitation code isn't valid for this email. Please check the code your administrator gave you."
            );
          }

          await signUp(email, values.password);
          navigate("/");
        } catch (error: any) {
          const message =
            error?.response?.data?.error ||
            error?.message ||
            "Something went wrong";
          setStatus({ success: false });
          setErrors({ submit: message });
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          {errors.submit && (
            <Alert mt={2} mb={1} severity="warning">
              {errors.submit}
            </Alert>
          )}
          <TextField
            type="email"
            name="email"
            label="Email address"
            value={values.email}
            error={Boolean(touched.email && errors.email)}
            fullWidth
            helperText={touched.email && errors.email}
            onBlur={handleBlur}
            onChange={handleChange}
            my={3}
          />
          <TextField
            type="text"
            name="invitationCode"
            label="Invitation code"
            value={values.invitationCode}
            error={Boolean(touched.invitationCode && errors.invitationCode)}
            fullWidth
            helperText={
              (touched.invitationCode && errors.invitationCode) ||
              "Enter the code your administrator gave you"
            }
            onBlur={handleBlur}
            onChange={handleChange}
            inputProps={{ style: { textTransform: "uppercase" } }}
            my={3}
          />
          <TextField
            type="password"
            name="password"
            label="Password"
            value={values.password}
            error={Boolean(touched.password && errors.password)}
            fullWidth
            helperText={touched.password && errors.password}
            onBlur={handleBlur}
            onChange={handleChange}
            my={3}
          />
          <TextField
            type="password"
            name="confirmPassword"
            label="Confirm password"
            value={values.confirmPassword}
            error={Boolean(touched.confirmPassword && errors.confirmPassword)}
            fullWidth
            helperText={touched.confirmPassword && errors.confirmPassword}
            onBlur={handleBlur}
            onChange={handleChange}
            my={3}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            mb={3}
          >
            Sign up
          </Button>
          <Centered>
            Already have an account?{" "}
            <Link to="../sign-in" component={RouterLink}>
              Log in
            </Link>
          </Centered>
        </form>
      )}
    </Formik>
  );
}

export default SignUp;
